import {
  loadStripe,
  PaymentMethodResult,
  Stripe,
  StripeCardElement,
  StripeElements,
  StripeElementsOptions,
} from '@stripe/stripe-js'
import { ref } from 'vue'

const defaultElementsOptions: StripeElementsOptions = {
  mode: 'payment',
  loader: 'always',
  locale: 'en',
  currency: 'aud',
  payment_method_types: ['card'],
  paymentMethodCreation: 'manual',
}

const stripeInstance = ref<Stripe | null>(null)
const elementsInstance = ref<StripeElements | null>(null)
const isReady = ref(false)
const error = ref<string | null>(null)

const paymentElement = ref<StripeCardElement | null>(null) // assuming StripeCardElement is imported
const paymentElementMounted = ref(false)

export const useStripe = () => {
  const getStripe = async (): Promise<Stripe | null> => {
    if (!stripeInstance.value) {
      try {
        stripeInstance.value = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, {
          locale: 'en',
        })
        isReady.value = true
        console.log('Stripe loaded successfully')
      } catch (err) {
        error.value = 'Failed to load Stripe'
        console.error(err)
      }
    }
    return stripeInstance.value
  }

  const initElements = async (options?: StripeElementsOptions): Promise<StripeElements | null> => {
    const stripe = await getStripe()
    if (!stripe) {
      error.value = 'Stripe is not initialized'
      return null
    }

    const mergedOptions: StripeElementsOptions = {
      ...defaultElementsOptions,
      ...options,
    } as StripeElementsOptions
    if (!elementsInstance.value) {
      elementsInstance.value = stripe.elements(mergedOptions)
    }
    return elementsInstance.value
  }

  const payByCard = async (): Promise<PaymentMethodResult | null> => {
    const stripe = await getStripe()
    if (!stripe || !elementsInstance.value) {
      error.value = 'Stripe is not initialized'
      return null
    }

    try {
      elementsInstance.value.submit
      const result = await stripe.createPaymentMethod({
        elements: elementsInstance.value,
      })
      if (!result) {
        error.value = 'No result returned from Stripe'
        return null
      }

      if (result.error || !result.paymentMethod) {
        error.value = result.error.message || 'Failed to create payment method'
        return null
      }

      return result
    } catch (err) {
      error.value = 'An error occurred during payment'
      console.error(err)
      return null
    }
  }

  const createPaymentElement = async (options?: StripeElementsOptions) => {
    const elements = await initElements(options)
    if (!elements || elements.getElement('payment')) return null
    return elements.create('payment')
  }

  /**
   * Mounts a new payment element to a container and sets tracking refs.
   * @param containerId - The ID of the container where the payment element will be mounted.
   * @param options - Optional configuration for the payment element.
   * @returns The mounted payment element or null if mounting fails.
   */
  const mountPaymentElement = async (containerId: string, options?: StripeElementsOptions) => {
    const element = await createPaymentElement(options)
    if (element) {
      element.mount(containerId)
      paymentElement.value = element
      paymentElementMounted.value = true
      return element
    }
    return null
  }

  /**
   * Unmounts the currently mounted payment element and resets tracking refs.
   */
  const unmountPaymentElement = () => {
    if (paymentElement.value && paymentElementMounted.value) {
      paymentElement.value.unmount()
      paymentElement.value = null
      paymentElementMounted.value = false
    }
  }

  /**
   * Checks if a payment element is currently mounted.
   * @returns True if a payment element is mounted, otherwise false.
   */
  const isPaymentMounted = () => paymentElementMounted.value

  // New function to reinitialize Stripe and payment elements
  const reinitialize = async (): Promise<Stripe | null> => {
    // Unmount any mounted payment element
    unmountPaymentElement()
    // Reset internal refs
    stripeInstance.value = null
    elementsInstance.value = null
    isReady.value = false
    error.value = null
    // Optionally, reset any other related refs if needed
    // (e.g. paymentElement and paymentElementMounted are reset in unmountPaymentElement)
    return await getStripe()
  }

  return {
    getStripe,
    initElements,
    createPaymentElement,
    mountPaymentElement,
    unmountPaymentElement,
    isPaymentMounted,
    reinitialize,
    payByCard,
    stripe: stripeInstance,
    elements: elementsInstance,
    isReady,
    error,
  }
}
